import { tokens } from '../design-system/tokens';

interface LimitDisplayProps {
  messagesUsed: number;
  limit: number;
  messagesRemaining: number;
  daysRemaining?: number;
  isUnavailable?: boolean;
  /** Callback shown as "Solicitar Demo" button when messages reach 0 on landing */
  onScheduleDemo?: () => void;
}

/**
 * Returns the progress bar color based on usage percentage:
 * - Green: < 70% used
 * - Orange/warning: 70–99% used
 * - Red: 100% used (0 remaining)
 */
function getBarColor(messagesRemaining: number, limit: number): string {
  if (limit <= 0 || messagesRemaining <= 0) return tokens.colors.error.main;
  const usedPercent = ((limit - messagesRemaining) / limit) * 100;
  if (usedPercent >= 70) return tokens.colors.warning.main;
  return tokens.colors.success.main;
}

export function LimitDisplay({ messagesUsed: _messagesUsed, limit, messagesRemaining, daysRemaining, isUnavailable, onScheduleDemo }: LimitDisplayProps) {
  if (isUnavailable) {
    return (
      <span
        style={{
          fontSize: tokens.typography.fontSize.sm,
          color: tokens.colors.neutral[400],
          cursor: 'default',
        }}
        title="Información de límite no disponible"
      >
        ℹ️
      </span>
    );
  }

  const barColor = getBarColor(messagesRemaining, limit);
  const fillPercent = limit > 0 ? Math.max(0, Math.min(100, (messagesRemaining / limit) * 100)) : 0;
  const showDemoButton = messagesRemaining <= 0 && onScheduleDemo && daysRemaining === undefined;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      {/* Progress + counter */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '3px',
          minWidth: '70px',
        }}
      >
        {/* Counter text */}
        <span
          style={{
            fontSize: '0.75rem',
            color: barColor,
            fontWeight: 600,
            lineHeight: 1,
            transition: 'color 0.3s ease',
          }}
        >
          {messagesRemaining} / {limit}
          {daysRemaining !== undefined && (
            <span
              style={{
                marginLeft: '4px',
                fontSize: '0.65rem',
                fontWeight: 500,
                color: daysRemaining <= 1 ? tokens.colors.error.main : tokens.colors.neutral[400],
              }}
            >
              · {daysRemaining}d
            </span>
          )}
        </span>

        {/* Progress bar */}
        <div
          style={{
            width: '100%',
            height: '3px',
            borderRadius: '2px',
            backgroundColor: tokens.colors.neutral[200],
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${fillPercent}%`,
              height: '100%',
              borderRadius: '2px',
              backgroundColor: barColor,
              transition: 'width 0.4s ease, background-color 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Solicitar Demo button — shown when landing reaches 0 */}
      {showDemoButton && (
        <button
          onClick={onScheduleDemo}
          style={{
            padding: '4px 10px',
            fontSize: '0.7rem',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: tokens.colors.primary[600],
            border: 'none',
            borderRadius: tokens.borderRadius.md,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: `background-color ${tokens.transitions.fast}`,
          }}
        >
          Solicitar Demo
        </button>
      )}
    </div>
  );
}
